import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

interface ConferenceItem {
  id: string;
  year: string;
  venue: string;
  conferenceName: string;
  eventName: string;
  title: string;
  presenter: string;
  createdAt: number;
}

interface PaperItem {
  id: string;
  journal: string;
  title: string;
  authors: string;
  coAuthors: string;
  pmidOrDoi: string;
  createdAt: number;
}

interface CollaborativeItem {
  id: string;
  year: string;
  institution: string;
  title: string;
  isSubResearch: boolean;
  createdAt: number;
}

const Publications = () => {
  const [conferenceItems, setConferenceItems] = useState<ConferenceItem[]>([]);
  const [paperItems, setPaperItems] = useState<PaperItem[]>([]);
  const [collaborativeItems, setCollaborativeItems] = useState<CollaborativeItem[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [activeTab, setActiveTab] = useState<'conference' | 'paper' | 'collaborative'>('conference');
  const [editingItem, setEditingItem] = useState<ConferenceItem | PaperItem | CollaborativeItem | null>(null);

  const [conferenceForm, setConferenceForm] = useState({
    year: '',
    venue: '',
    conferenceName: '',
    eventName: '',
    title: '',
    presenter: ''
  });

  const [paperForm, setPaperForm] = useState({
    journal: '',
    title: '',
    authors: '',
    coAuthors: '',
    pmidOrDoi: ''
  });

  const [collaborativeForm, setCollaborativeForm] = useState({
    year: '',
    institution: '',
    title: '',
    isSubResearch: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const storedConference = localStorage.getItem('conferenceItems');
    const storedPaper = localStorage.getItem('paperItems');
    const storedCollaborative = localStorage.getItem('collaborativeItems');

    if (storedConference) {
      const parsed = JSON.parse(storedConference);
      setConferenceItems(parsed.sort((a: ConferenceItem, b: ConferenceItem) => b.createdAt - a.createdAt));
    }
    if (storedPaper) {
      const parsed = JSON.parse(storedPaper);
      setPaperItems(parsed.sort((a: PaperItem, b: PaperItem) => b.createdAt - a.createdAt));
    }
    if (storedCollaborative) {
      const parsed = JSON.parse(storedCollaborative);
      setCollaborativeItems(parsed.sort((a: CollaborativeItem, b: CollaborativeItem) => b.createdAt - a.createdAt));
    }
  };

  const handleAdminLogin = () => {
    if (password === 'cr.toranomon') {
      setIsAdmin(true);
      setPassword('');
    } else {
      alert('パスワードが正しくありません');
    }
  };

  const handleConferenceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingItem && 'venue' in editingItem) {
      const updated = conferenceItems.map(item => 
        item.id === editingItem.id 
          ? { ...item, ...conferenceForm }
          : item
      );
      localStorage.setItem('conferenceItems', JSON.stringify(updated));
      setConferenceItems(updated);
      setEditingItem(null);
      alert('学会発表を更新しました');
    } else {
      const newItem: ConferenceItem = {
        id: Date.now().toString(),
        ...conferenceForm,
        createdAt: Date.now()
      };
      const updated = [newItem, ...conferenceItems];
      localStorage.setItem('conferenceItems', JSON.stringify(updated));
      setConferenceItems(updated);
      alert('学会発表を登録しました');
    }
    
    setConferenceForm({ year: '', venue: '', conferenceName: '', eventName: '', title: '', presenter: '' });
  };

  const handlePaperSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingItem && 'journal' in editingItem) {
      const updated = paperItems.map(item => 
        item.id === editingItem.id 
          ? { ...item, ...paperForm }
          : item
      );
      localStorage.setItem('paperItems', JSON.stringify(updated));
      setPaperItems(updated);
      setEditingItem(null);
      alert('論文発表を更新しました');
    } else {
      const newItem: PaperItem = {
        id: Date.now().toString(),
        ...paperForm,
        createdAt: Date.now()
      };
      const updated = [newItem, ...paperItems];
      localStorage.setItem('paperItems', JSON.stringify(updated));
      setPaperItems(updated);
      alert('論文発表を登録しました');
    }
    
    setPaperForm({ journal: '', title: '', authors: '', coAuthors: '', pmidOrDoi: '' });
  };

  const handleCollaborativeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingItem && 'institution' in editingItem) {
      const updated = collaborativeItems.map(item => 
        item.id === editingItem.id 
          ? { ...item, ...collaborativeForm }
          : item
      );
      localStorage.setItem('collaborativeItems', JSON.stringify(updated));
      setCollaborativeItems(updated);
      setEditingItem(null);
      alert('多施設共同研究を更新しました');
    } else {
      const newItem: CollaborativeItem = {
        id: Date.now().toString(),
        ...collaborativeForm,
        createdAt: Date.now()
      };
      const updated = [newItem, ...collaborativeItems];
      localStorage.setItem('collaborativeItems', JSON.stringify(updated));
      setCollaborativeItems(updated);
      alert('多施設共同研究を登録しました');
    }
    
    setCollaborativeForm({ year: '', institution: '', title: '', isSubResearch: false });
  };

  const handleEdit = (item: ConferenceItem | PaperItem | CollaborativeItem, type: 'conference' | 'paper' | 'collaborative') => {
    setEditingItem(item);
    setActiveTab(type);
    
    if (type === 'conference' && 'venue' in item) {
      setConferenceForm({
        year: item.year,
        venue: item.venue,
        conferenceName: item.conferenceName,
        eventName: item.eventName,
        title: item.title,
        presenter: item.presenter
      });
    } else if (type === 'paper' && 'journal' in item) {
      setPaperForm({
        journal: item.journal,
        title: item.title,
        authors: item.authors,
        coAuthors: item.coAuthors,
        pmidOrDoi: item.pmidOrDoi
      });
    } else if (type === 'collaborative' && 'institution' in item) {
      setCollaborativeForm({
        year: item.year,
        institution: item.institution,
        title: item.title,
        isSubResearch: item.isSubResearch
      });
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setConferenceForm({ year: '', venue: '', conferenceName: '', eventName: '', title: '', presenter: '' });
    setPaperForm({ journal: '', title: '', authors: '', coAuthors: '', pmidOrDoi: '' });
    setCollaborativeForm({ year: '', institution: '', title: '', isSubResearch: false });
  };

  const handleDelete = (id: string, type: 'conference' | 'paper' | 'collaborative') => {
    if (!window.confirm('この項目を削除しますか？')) return;

    if (type === 'conference') {
      const updated = conferenceItems.filter(item => item.id !== id);
      localStorage.setItem('conferenceItems', JSON.stringify(updated));
      setConferenceItems(updated);
    } else if (type === 'paper') {
      const updated = paperItems.filter(item => item.id !== id);
      localStorage.setItem('paperItems', JSON.stringify(updated));
      setPaperItems(updated);
    } else {
      const updated = collaborativeItems.filter(item => item.id !== id);
      localStorage.setItem('collaborativeItems', JSON.stringify(updated));
      setCollaborativeItems(updated);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              学会発表・論文
            </h1>
            <p className="text-lg text-gray-700 max-w-3xl">
              虎の門病院循環器センター・心臓リハビリテーションチームの研究成果を、学会発表、論文発表、多施設共同研究に分けてご紹介します。
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Admin Login */}
            {!isAdmin && (
              <div className="mb-8 text-right">
                <button
                  onClick={() => setShowAdminPanel(!showAdminPanel)}
                  className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  管理者ログイン
                </button>
              </div>
            )}

            {showAdminPanel && !isAdmin && (
              <div className="mb-12 bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">管理者ログイン</h3>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワードを入力"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                  />
                  <button
                    onClick={handleAdminLogin}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap text-sm cursor-pointer"
                  >
                    ログイン
                  </button>
                </div>
              </div>
            )}

            {/* Admin Panel */}
            {isAdmin && (
              <div className="mb-12 bg-teal-50 rounded-lg p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editingItem ? '項目を編集' : '新しい項目を登録'}
                  </h3>
                  <button
                    onClick={() => setIsAdmin(false)}
                    className="text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
                  >
                    ログアウト
                  </button>
                </div>

                {/* Tab Selector */}
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setActiveTab('conference')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'conference'
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    学会発表
                  </button>
                  <button
                    onClick={() => setActiveTab('paper')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'paper'
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    論文発表
                  </button>
                  <button
                    onClick={() => setActiveTab('collaborative')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'collaborative'
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    多施設共同研究
                  </button>
                </div>

                {/* Conference Form */}
                {activeTab === 'conference' && (
                  <form onSubmit={handleConferenceSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          年度
                        </label>
                        <input
                          type="text"
                          value={conferenceForm.year}
                          onChange={(e) => setConferenceForm({ ...conferenceForm, year: e.target.value })}
                          placeholder="例：2024年"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          会場
                        </label>
                        <input
                          type="text"
                          value={conferenceForm.venue}
                          onChange={(e) => setConferenceForm({ ...conferenceForm, venue: e.target.value })}
                          placeholder="例：東京国際フォーラム"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          学会名
                        </label>
                        <input
                          type="text"
                          value={conferenceForm.conferenceName}
                          onChange={(e) => setConferenceForm({ ...conferenceForm, conferenceName: e.target.value })}
                          placeholder="例：日本循環器学会"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          大会名
                        </label>
                        <input
                          type="text"
                          value={conferenceForm.eventName}
                          onChange={(e) => setConferenceForm({ ...conferenceForm, eventName: e.target.value })}
                          placeholder="例：第88回日本循環器学会学術集会"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        研究タイトル
                      </label>
                      <input
                        type="text"
                        value={conferenceForm.title}
                        onChange={(e) => setConferenceForm({ ...conferenceForm, title: e.target.value })}
                        placeholder="研究のタイトルを入力"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        筆頭者
                      </label>
                      <input
                        type="text"
                        value={conferenceForm.presenter}
                        onChange={(e) => setConferenceForm({ ...conferenceForm, presenter: e.target.value })}
                        placeholder="例：山田太郎"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                      >
                        {editingItem ? '更新する' : '登録する'}
                      </button>
                      {editingItem && (
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium whitespace-nowrap cursor-pointer"
                        >
                          キャンセル
                        </button>
                      )}
                    </div>
                  </form>
                )}

                {/* Paper Form */}
                {activeTab === 'paper' && (
                  <form onSubmit={handlePaperSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        雑誌情報
                      </label>
                      <input
                        type="text"
                        value={paperForm.journal}
                        onChange={(e) => setPaperForm({ ...paperForm, journal: e.target.value })}
                        placeholder="例：Circulation Journal, 2024;88(5):123-130"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        研究タイトル
                      </label>
                      <input
                        type="text"
                        value={paperForm.title}
                        onChange={(e) => setPaperForm({ ...paperForm, title: e.target.value })}
                        placeholder="論文のタイトルを入力"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        著者
                      </label>
                      <input
                        type="text"
                        value={paperForm.authors}
                        onChange={(e) => setPaperForm({ ...paperForm, authors: e.target.value })}
                        placeholder="例：山田太郎"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        共著者
                      </label>
                      <input
                        type="text"
                        value={paperForm.coAuthors}
                        onChange={(e) => setPaperForm({ ...paperForm, coAuthors: e.target.value })}
                        placeholder="例：佐藤花子、鈴木一郎"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        PMIDまたはDOI
                      </label>
                      <input
                        type="text"
                        value={paperForm.pmidOrDoi}
                        onChange={(e) => setPaperForm({ ...paperForm, pmidOrDoi: e.target.value })}
                        placeholder="例：PMID: 12345678 または DOI: 10.1234/example"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                      >
                        {editingItem ? '更新する' : '登録する'}
                      </button>
                      {editingItem && (
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium whitespace-nowrap cursor-pointer"
                        >
                          キャンセル
                        </button>
                      )}
                    </div>
                  </form>
                )}

                {/* Collaborative Form */}
                {activeTab === 'collaborative' && (
                  <form onSubmit={handleCollaborativeSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          年度
                        </label>
                        <input
                          type="text"
                          value={collaborativeForm.year}
                          onChange={(e) => setCollaborativeForm({ ...collaborativeForm, year: e.target.value })}
                          placeholder="例：2024年"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          主たる研究機関
                        </label>
                        <input
                          type="text"
                          value={collaborativeForm.institution}
                          onChange={(e) => setCollaborativeForm({ ...collaborativeForm, institution: e.target.value })}
                          placeholder="例：虎の門病院"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        研究タイトル
                      </label>
                      <input
                        type="text"
                        value={collaborativeForm.title}
                        onChange={(e) => setCollaborativeForm({ ...collaborativeForm, title: e.target.value })}
                        placeholder="研究のタイトルを入力"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={collaborativeForm.isSubResearch}
                          onChange={(e) => setCollaborativeForm({ ...collaborativeForm, isSubResearch: e.target.checked })}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                        />
                        <span className="text-sm font-medium text-gray-700">分担研究</span>
                      </label>
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                      >
                        {editingItem ? '更新する' : '登録する'}
                      </button>
                      {editingItem && (
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium whitespace-nowrap cursor-pointer"
                        >
                          キャンセル
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Conference Presentations */}
            <section id="conference" className="mb-20">
              <div className="flex items-center gap-4 mb-12">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-presentation-line text-2xl text-primary-600"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">学会発表</h2>
              </div>
              
              {conferenceItems.length === 0 ? (
                <div className="text-center py-16">
                  <i className="ri-presentation-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">まだ学会発表の登録はありません</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {conferenceItems.map((item) => (
                    <article
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start gap-6">
                        <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                          <i className="ri-presentation-line text-2xl text-primary-600"></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full whitespace-nowrap">
                                {item.year}
                              </span>
                              <span className="text-sm text-gray-500">{item.venue}</span>
                            </div>
                            {isAdmin && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEdit(item, 'conference')}
                                  className="text-teal-600 hover:text-teal-700 cursor-pointer p-2"
                                >
                                  <i className="ri-edit-line text-xl"></i>
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id, 'conference')}
                                  className="text-red-500 hover:text-red-700 cursor-pointer p-2"
                                >
                                  <i className="ri-delete-bin-line text-xl"></i>
                                </button>
                              </div>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                          <p className="text-gray-600 mb-2">{item.conferenceName} - {item.eventName}</p>
                          <p className="text-sm text-gray-500">筆頭者：{item.presenter}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            {/* Papers */}
            <section id="papers" className="mb-20">
              <div className="flex items-center gap-4 mb-12">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-file-text-line text-2xl text-primary-600"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">論文発表</h2>
              </div>
              
              {paperItems.length === 0 ? (
                <div className="text-center py-16">
                  <i className="ri-file-text-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">まだ論文発表の登録はありません</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {paperItems.map((item) => (
                    <article
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start gap-6">
                        <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                          <i className="ri-file-text-line text-2xl text-primary-600"></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <span className="text-sm text-gray-500">{item.journal}</span>
                            {isAdmin && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEdit(item, 'paper')}
                                  className="text-teal-600 hover:text-teal-700 cursor-pointer p-2"
                                >
                                  <i className="ri-edit-line text-xl"></i>
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id, 'paper')}
                                  className="text-red-500 hover:text-red-700 cursor-pointer p-2"
                                >
                                  <i className="ri-delete-bin-line text-xl"></i>
                                </button>
                              </div>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                          <p className="text-gray-600 mb-2">
                            著者：{item.authors}
                            {item.coAuthors && ` / 共著者：${item.coAuthors}`}
                          </p>
                          <p className="text-sm text-gray-500">{item.pmidOrDoi}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            {/* Collaborative Research */}
            <section id="collaborative" className="mb-20">
              <div className="flex items-center gap-4 mb-12">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-team-line text-2xl text-primary-600"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">多施設共同研究</h2>
              </div>
              
              {collaborativeItems.length === 0 ? (
                <div className="text-center py-16">
                  <i className="ri-team-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">まだ多施設共同研究の登録はありません</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {collaborativeItems.map((item) => (
                    <article
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start gap-6">
                        <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                          <i className="ri-team-line text-2xl text-primary-600"></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full whitespace-nowrap">
                                {item.year}
                              </span>
                              {item.isSubResearch && (
                                <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full whitespace-nowrap">
                                  分担研究
                                </span>
                              )}
                            </div>
                            {isAdmin && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEdit(item, 'collaborative')}
                                  className="text-teal-600 hover:text-teal-700 cursor-pointer p-2"
                                >
                                  <i className="ri-edit-line text-xl"></i>
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id, 'collaborative')}
                                  className="text-red-500 hover:text-red-700 cursor-pointer p-2"
                                >
                                  <i className="ri-delete-bin-line text-xl"></i>
                                </button>
                              </div>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                          <p className="text-gray-600">主たる研究機関：{item.institution}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="bg-gradient-to-br from-teal-50 to-white p-10 rounded-2xl border border-teal-100">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-arrow-left-line text-3xl text-white"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                研究活動ページに戻る
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                その他の研究活動については、研究活動ページをご覧ください。
              </p>
              <Link
                to="/research"
                className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 text-white text-base font-medium rounded-lg hover:bg-teal-600 transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                研究活動ページへ
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Publications;
